// Miniatura de un archivo: foto, fotograma de video o ícono de documento.
//
// Con `ampliable`, al pasar el mouse (tras ~150 ms) aparece una vista ampliada flotante. Ahorro de
// datos: la ampliación usa SOLO la miniatura (`thumbUrl`, 400 px) agrandada, nunca la URL del
// original; un archivo sin miniatura muestra un ícono grande con su nombre. Sin puntero que haga
// hover (celulares, tablets) no se monta nada: cero peticiones a /files por esto.
import React, { useState } from "react";
import { Popover, Typography } from "antd";
import {
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  PlayCircleFilled,
  VideoCameraOutlined,
} from "@ant-design/icons";
import type { Archivo } from "../../../services/almacenamiento.api";
import { puedeHover } from "../aperturaArchivo";

const { Text } = Typography;

/** Retraso (segundos) antes de mostrar la ampliación: evita el parpadeo al cruzar la lista. */
export const RETARDO_AMPLIACION_S = 0.15;

/** Ícono según el tipo MIME / tipo de archivo. */
export function iconoDeArchivo(a: Pick<Archivo, "tipo" | "mime">): React.ReactNode {
  const mime = (a.mime || "").toLowerCase();
  if (a.tipo === "image") return <FileImageOutlined />;
  if (a.tipo === "video") return <VideoCameraOutlined />;
  if (mime.includes("pdf")) return <FilePdfOutlined />;
  if (mime.includes("sheet") || mime.includes("excel") || mime.includes("csv")) {
    return <FileExcelOutlined />;
  }
  if (mime.includes("word") || mime.includes("document")) return <FileWordOutlined />;
  if (mime.includes("zip") || mime.includes("compressed")) return <FileZipOutlined />;
  if (mime.startsWith("text/")) return <FileTextOutlined />;
  return <FileOutlined />;
}

type DatosMiniatura = Pick<Archivo, "nombre" | "tipo" | "mime" | "thumbUrl" | "url" | "estado">;

/** Contenido flotante: la miniatura agrandada (o ícono grande + nombre si no hay miniatura). */
export const AmpliacionMiniatura: React.FC<{ archivo: DatosMiniatura }> = ({ archivo }) => {
  const [fallo, setFallo] = useState(false);
  // Solo `thumbUrl`: jamás el original (`url`) por un simple hover.
  const src = archivo.thumbUrl && !fallo ? archivo.thumbUrl : null;
  return (
    <div className="alm-ampliacion-cuerpo" data-testid="alm-ampliacion">
      {src ? (
        <span className="alm-ampliacion-marco">
          <img
            className="alm-ampliacion-img"
            src={src}
            alt=""
            data-testid="alm-ampliacion-img"
            onError={() => setFallo(true)}
          />
          {archivo.tipo === "video" && <PlayCircleFilled className="alm-ampliacion-play" />}
        </span>
      ) : (
        <span className="alm-ampliacion-icono" data-testid="alm-ampliacion-icono">
          {iconoDeArchivo(archivo)}
        </span>
      )}
      <Text className="alm-ampliacion-nombre">{archivo.nombre}</Text>
    </div>
  );
};

interface Props {
  archivo: DatosMiniatura;
  /** Lado del cuadrado en px. */
  size?: number;
  /** Al pasar el mouse muestra la miniatura ampliada (solo con puntero que haga hover). */
  ampliable?: boolean;
  /**
   * Clic con el mouse sobre la miniatura. Es un atajo: la miniatura no es un control de
   * teclado (para eso está el botón «Abrir» de la fila).
   */
  onAbrir?: () => void;
}

const ArchivoMiniatura: React.FC<Props> = ({ archivo, size = 56, ampliable = false, onAbrir }) => {
  const [fallo, setFallo] = useState(false);
  // Se evalúa una vez al montar: un dispositivo no cambia de tipo de puntero a mitad de lista.
  const [conHover] = useState<boolean>(puedeHover);
  // Una foto sin miniatura (o con la miniatura rota) muestra la foto completa;
  // un video sin fotograma, el ícono.
  const src =
    archivo.estado === "roto"
      ? null
      : archivo.thumbUrl || (archivo.tipo === "image" ? archivo.url : null);
  const mostrarImagen = Boolean(src) && !fallo;

  const mini = (
    <span
      className={`alm-mini${archivo.estado === "roto" ? " alm-mini-roto" : ""}${onAbrir ? " alm-abrible" : ""}`}
      style={{ width: size, height: size, minWidth: size }}
      aria-hidden
      onClick={onAbrir}
    >
      {mostrarImagen ? (
        <img
          src={src as string}
          alt=""
          loading="lazy"
          onError={() => setFallo(true)}
        />
      ) : (
        <span className="alm-mini-icono" style={{ fontSize: Math.round(size * 0.45) }}>
          {iconoDeArchivo(archivo)}
        </span>
      )}
      {archivo.tipo === "video" && mostrarImagen && (
        <PlayCircleFilled className="alm-mini-play" />
      )}
    </span>
  );

  // Una referencia rota no tiene nada que ampliar; sin hover no se monta el Popover.
  if (!ampliable || !conHover || archivo.estado === "roto") return mini;

  return (
    <Popover
      content={<AmpliacionMiniatura archivo={archivo} />}
      mouseEnterDelay={RETARDO_AMPLIACION_S}
      mouseLeaveDelay={0}
      trigger="hover"
      placement="right"
      destroyTooltipOnHide
      overlayClassName="alm-ampliacion"
    >
      {mini}
    </Popover>
  );
};

export default ArchivoMiniatura;
